import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import TrackingProgressScreen from './TrackingProgressScreen'
import { useMorningFlow } from '../../hooks/useMorningFlow'
import {
  briefingKeyNutrition,
  isBriefingRead,
  markBriefingRead,
} from '../../lib/coachBriefingReadState'
import { getCalendarDateForDayId, getTodayDayId } from '../../lib/days'
import { buildDailySummaryFromMeals, loadMealLogsForDate } from '../../lib/mealLog'
import { sumMacroTotals } from '../../lib/mealLogCore'
import { loadProfileStats } from '../../lib/profileStats'
import { useTodayProtocol } from '../../lib/useTodayProtocol'
import { KILO } from '../../constants/KILO_THEME'
import type { CoachHealthSummary } from '../../lib/coachHealthTypes'
import type { DailyNutritionSummary } from '../../lib/mealLogCore'
import type { MealBlock } from '../../lib/protocolTypes'
import { SkeletonCard } from '../ui/Skeleton'

type TrackingProgressCardProps = {
  summary: CoachHealthSummary | null
  loading: boolean
  userId?: string | null
  meals?: MealBlock[]
}

function yesterdayDateId(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().split('T')[0]
}

const TrackingProgressCard = ({
  summary,
  loading,
  userId,
  meals = [],
}: TrackingProgressCardProps) => {
  const { t } = useTranslation()
  const [readVersion, setReadVersion] = useState(0)
  const [panelOpen, setPanelOpen] = useState(false)
  const [exercisesCompleted, setExercisesCompleted] = useState(0)
  const [exercisesTotal, setExercisesTotal] = useState(0)
  const [trainingStreakDays, setTrainingStreakDays] = useState(0)
  const [todaySummary, setTodaySummary] = useState<DailyNutritionSummary | null>(null)

  const visibleMetrics = summary?.visible_metrics ?? []
  const showMorningBrief = visibleMetrics.includes('body_state_briefing')
  const hasMorningText = Boolean(summary?.todays_briefing?.briefing_text)
  const { todayProtocol } = useTodayProtocol(Boolean(summary))
  const morningFlow = useMorningFlow({
    summary,
    todayProtocol,
    showMorningBrief,
    hasMorningText,
  })

  const nonWearableBriefingActive =
    !morningFlow.hasWearable && morningFlow.activeStep === 'briefing'

  const yesterdaySummary = summary?.nutrition_yesterday ?? null
  const hasLoggedYesterday = (yesterdaySummary?.slots_logged ?? 0) > 0
  const nutritionDateId = yesterdayDateId()

  const weekTargetKcal = useMemo(
    () =>
      sumMacroTotals(
        meals.map((m) => ({
          calories_kcal: m.calories_kcal,
          protein_g: m.protein_g,
          carbs_g: m.carbs_g,
          fat_g: m.fat_g,
        })),
      ).calories_kcal,
    [meals],
  )

  const weekActualKcal = useMemo(() => {
    const todayKcal = todaySummary?.actual.calories_kcal ?? 0
    const yesterdayKcal = yesterdaySummary?.actual.calories_kcal ?? 0
    return todayKcal + yesterdayKcal
  }, [todaySummary, yesterdaySummary])

  const todayPlannedOnly = useMemo(() => {
    if (meals.length === 0) return null
    const todayId = getTodayDayId()
    const todayDate = getCalendarDateForDayId(todayId)
    const dayMeals = meals.filter((m) => m.day_id === todayId)
    if (dayMeals.length === 0) return null
    return buildDailySummaryFromMeals(todayDate, dayMeals, [])
  }, [meals])

  const effectiveTodaySummary = todaySummary ?? todayPlannedOnly

  useEffect(() => {
    if (!userId || meals.length === 0) {
      setTodaySummary(null)
      return
    }

    let cancelled = false
    const todayId = getTodayDayId()
    const todayDate = getCalendarDateForDayId(todayId)
    const dayMeals = meals.filter((m) => m.day_id === todayId)

    void loadMealLogsForDate(userId, todayDate).then(({ logs }) => {
      if (cancelled) return
      setTodaySummary(buildDailySummaryFromMeals(todayDate, dayMeals, logs))
    })

    return () => {
      cancelled = true
    }
  }, [userId, meals])

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    void loadProfileStats(userId).then(({ stats }) => {
      if (cancelled || !stats) return
      setExercisesCompleted(stats.exercises_completed)
      setExercisesTotal(stats.exercises_total)
      setTrainingStreakDays(stats.training_streak_days)
    })
    return () => {
      cancelled = true
    }
  }, [userId, summary])

  const badge = useMemo(() => {
    void readVersion
    if (!hasLoggedYesterday) return 0
    return isBriefingRead(briefingKeyNutrition(nutritionDateId)) ? 0 : 1
  }, [readVersion, hasLoggedYesterday, nutritionDateId])

  const hasNutritionContent =
    effectiveTodaySummary != null ||
    yesterdaySummary != null ||
    meals.length > 0

  if (loading && !summary) {
    return (
      <div role="status">
        <SkeletonCard />
      </div>
    )
  }

  if (!summary || nonWearableBriefingActive || !hasNutritionContent) return null

  const title = t('coach.sections.trackingProgress')
  const teaser = t('coach.sections.trackingProgressTeaser')

  const markRead = () => {
    if (hasLoggedYesterday) markBriefingRead(briefingKeyNutrition(nutritionDateId))
    setReadVersion((v) => v + 1)
  }

  const openPanel = () => {
    markRead()
    setPanelOpen(true)
  }

  return (
    <>
      <BentoCard gradient="none" className="!p-0 overflow-hidden">
        <button
          type="button"
          onClick={openPanel}
          className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-kilo-text/[0.02]"
          aria-expanded={panelOpen}
        >
          <MaterialIcon name="monitoring" hoverable size="sm" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={KILO.meta}>{title}</p>
              {badge > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-kilo-accent px-1.5 font-mono text-[10px] font-bold text-black">
                  {badge}
                </span>
              ) : null}
            </div>
            <p className={`mt-1 truncate text-sm leading-relaxed ${KILO.text.muted}`}>{teaser}</p>
          </div>
          <MaterialIcon name="chevron_right" size="sm" className="shrink-0" />
        </button>
      </BentoCard>

      <TrackingProgressScreen
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        todaySummary={effectiveTodaySummary}
        weekTargetKcal={weekTargetKcal}
        weekActualKcal={weekActualKcal}
        exercisesCompleted={exercisesCompleted}
        exercisesTotal={exercisesTotal}
        trainingStreakDays={trainingStreakDays}
        userId={userId}
        meals={meals}
      />
    </>
  )
}

export default TrackingProgressCard
